import React, { useEffect, useMemo, useState } from "react";
import { CarouselComponent } from "../../../ui-components/Carousel/Carousel";
import store from "../../../appStore";
import { useTranslation } from "react-i18next";
import { getUrl } from "../../../utils";
import useAuth from "../../../hooks/useAuth";
import Loader from "../../../components/ui/Loader";
import {
  ContentPagePlaylist,
  ImageFileLanguage,
  LazyContentPagePlaylist,
  LazyPlaylist,
  LazyPlaylistImageFile,
  LazyPlaylistVideoFile,
  LazyVideoFileLanguage,
  Playlist,
  PlaylistImageFile,
  PlaylistVideoFile,
  VideoFileLanguage,
} from "../../../models/index.js";
import { DataStore } from "aws-amplify/datastore";

const CarouselWrapper = ({ contentId, content, showSidebar }) => {
  const [slidesData, setSlidesData] = useState<{ id: string }[]>([]);
  const [sequence, setSequence] = useState<string[]>([]);
  const { i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [playlist, setPlaylist] = useState<LazyPlaylist>();
  const [playlistVideoFile, setPlaylistVideoFile] = useState<LazyPlaylistVideoFile[]>();
  const [playlistImageFiles, setPlaylistImageFiles] = useState<LazyPlaylistImageFile[]>();

  useEffect(() => {
    if (!content) return;
    (async () => {
      const _contentPagePlaylist = (await DataStore.query(ContentPagePlaylist, (c) => c.contentPage.id.eq(content.id)))[0];
      const _videoFileLanguages = await DataStore.query(VideoFileLanguage, (c) => c.language.locale.eq(i18n.language));
      const _imageFileLanguages = await DataStore.query(ImageFileLanguage, (c) => c.language.locale.eq(i18n.language));

      if (_contentPagePlaylist) {
        const _playlist = (await DataStore.query(Playlist, (c) => c.id.eq(_contentPagePlaylist.playlistID)))[0];
        setPlaylist(_playlist);

        const _playlistVideoFiles = await DataStore.query(PlaylistVideoFile, (c) =>
          currentUser
            ? c.playlist.id.eq(_contentPagePlaylist.playlistID)
            : c.and((c) => [c.playlist.id.eq(_contentPagePlaylist.playlistID), c.videoFile.protected.eq(!!currentUser)]),
        );
        const _localizedVideoFiles = _playlistVideoFiles.filter((c) =>
          _videoFileLanguages.some((d) => d.videoFileID === c.videoFileID),
        );
        setPlaylistVideoFile(_localizedVideoFiles);

        const _playlistImageFile = await DataStore.query(PlaylistImageFile, (c) =>
          currentUser
            ? c.playlist.id.eq(_contentPagePlaylist.playlistID)
            : c.and((c) => [c.playlist.id.eq(_contentPagePlaylist.playlistID), c.imageFile.protected.eq(!!currentUser)]),
        );
        const _localizedImageFiles = _playlistImageFile.filter((c) =>
          _imageFileLanguages.some((d) => d.imageFileID === c.imageFileID),
        );
        setPlaylistImageFiles(_localizedImageFiles);
      }
    })();
  }, [content, currentUser, i18n.language]);

  useEffect(() => {
    playlist?.sequence?.forEach((id) => {
      let videoFile = playlistVideoFile?.find((plvf) => plvf.videoFileID === id);
      if (videoFile) {
        (async () => {
          try {
            const result = await getUrl({
              key: (await videoFile.videoFile).videoStorageKey,
              options: { accessLevel: "guest" },
            });
            const resultThumbnail = await getUrl({
              key: (await videoFile.videoFile).posterStorageKey,
              options: { accessLevel: "guest" },
            });
            setSlidesData((prevState) => {
              if (!result?.url || prevState.find((slide) => slide.id === videoFile.videoFileID)) {
                return prevState;
              } else {
                return [
                  ...prevState,
                  {
                    id: videoFile.videoFileID,
                    type: "videofile",
                    video: result.url.toString(),
                    thumbnail: resultThumbnail.url.toString(),
                  },
                ];
              }
            });
          } catch (err) {
            console.log(err);
          }
        })();
      }

      let imagefile = playlistImageFiles?.find((plvf) => plvf.imageFileID === id);
      if (imagefile) {
        (async () => {
          try {
            const result = await getUrl({
              key: (await imagefile.imageFile).imageStorageKey,
              options: { accessLevel: "guest" },
            });
            setSlidesData((prevState) => {
              if (!result?.url || prevState.find((slide) => slide.id === imagefile.imageFileID)) {
                return prevState;
              } else {
                return [
                  ...prevState,
                  {
                    id: imagefile.imageFileID,
                    type: "imagefile",
                    playlistfile: result.url.toString(),
                  },
                ];
              }
            });
          } catch (err) {
            console.log(err);
          }
        })();
      }
    });

    setSequence(playlist?.sequence as string[]);
  }, [playlist, playlistVideoFile, playlistImageFiles]);

  const slidesDataSorted = useMemo(() => {
    function sortFunc(a: { id: string }, b: { id: string }) {
      return sequence.indexOf(a.id) - sequence.indexOf(b.id);
    }

    return slidesData.sort(sortFunc);
  }, [sequence, slidesData]);

  return slidesDataSorted.length > 0 && showSidebar ? (
    <CarouselComponent
      playIconBackgroundColor="rgba(0,0,0, 0)"
      playIconBorderColor="#3EBA7C"
      data={slidesDataSorted}
      id={contentId}
    />
  ) : slidesDataSorted.length === 0 ? (
    <div style={{ color: "#fff", textAlign: "center", padding: "6.5vw 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "2vw",
          marginTop: "1vw",
        }}
      >
        <Loader backgroundColor="#000" />
      </div>
    </div>
  ) : null;
};

export default CarouselWrapper;
